/**
 * Helpers for the LaTeX equation editor: toolbar snippets, inserting one at the cursor,
 * and turning KaTeX's MathML into something Microsoft Word will paste as an equation.
 *
 * KaTeX itself is imported only by the widget, so it stays in that page's bundle.
 */

import { escapeHtml } from "@/lib/tool-kit/developer/markdown/text";

export interface EquationSnippet {
  /** Stable id; the widget names the snippet from its strings by this id. */
  id: string;
  /** What the button shows — a symbol, not a word. */
  label: string;
  /** Inserted before the selection; the cursor lands after it (and the selection). */
  before: string;
  /** Inserted after the selection. */
  after: string;
}

/** Templates: a selected term is wrapped, and the cursor lands in the first slot. */
export const STRUCTURE_SNIPPETS: readonly EquationSnippet[] = [
  { id: "fraction", label: "a⁄b", before: "\\frac{", after: "}{}" },
  { id: "sqrt", label: "√x", before: "\\sqrt{", after: "}" },
  { id: "root", label: "ⁿ√x", before: "\\sqrt[n]{", after: "}" },
  { id: "superscript", label: "xⁿ", before: "^{", after: "}" },
  { id: "subscript", label: "xₙ", before: "_{", after: "}" },
  { id: "sum", label: "∑", before: "\\sum_{i=1}^{n} ", after: "" },
  { id: "product", label: "∏", before: "\\prod_{i=1}^{n} ", after: "" },
  { id: "integral", label: "∫", before: "\\int ", after: " \\,dx" },
  { id: "definite", label: "∫ₐᵇ", before: "\\int_{a}^{b} ", after: " \\,dx" },
  { id: "limit", label: "lim", before: "\\lim_{x \\to \\infty} ", after: "" },
  { id: "parentheses", label: "( )", before: "\\left( ", after: " \\right)" },
  { id: "binomial", label: "(ⁿₖ)", before: "\\binom{n}{k}", after: "" },
  {
    id: "matrix",
    label: "[⋮]",
    before: "\\begin{pmatrix}\n  a & b \\\\\n  c & d\n\\end{pmatrix}",
    after: "",
  },
  {
    id: "cases",
    label: "{",
    before:
      "f(x) = \\begin{cases}\n  1 & x \\ge 0 \\\\\n  0 & x < 0\n\\end{cases}",
    after: "",
  },
  {
    id: "aligned",
    label: "=⋮",
    before: "\\begin{aligned}\n  a &= b + c \\\\\n    &= d\n\\end{aligned}",
    after: "",
  },
  { id: "text", label: "Aa", before: "\\text{", after: "}" },
];

function commands(
  pairs: readonly [string, string][]
): readonly EquationSnippet[] {
  return pairs.map(([command, label]) => ({
    id: command,
    label,
    before: `\\${command} `,
    after: "",
  }));
}

export const GREEK_SNIPPETS = commands([
  ["alpha", "α"],
  ["beta", "β"],
  ["gamma", "γ"],
  ["delta", "δ"],
  ["epsilon", "ϵ"],
  ["varepsilon", "ε"],
  ["zeta", "ζ"],
  ["eta", "η"],
  ["theta", "θ"],
  ["kappa", "κ"],
  ["lambda", "λ"],
  ["mu", "μ"],
  ["nu", "ν"],
  ["xi", "ξ"],
  ["pi", "π"],
  ["rho", "ρ"],
  ["sigma", "σ"],
  ["tau", "τ"],
  ["phi", "ϕ"],
  ["varphi", "φ"],
  ["chi", "χ"],
  ["psi", "ψ"],
  ["omega", "ω"],
  ["Gamma", "Γ"],
  ["Delta", "Δ"],
  ["Theta", "Θ"],
  ["Lambda", "Λ"],
  ["Sigma", "Σ"],
  ["Phi", "Φ"],
  ["Psi", "Ψ"],
  ["Omega", "Ω"],
]);

export const OPERATOR_SNIPPETS = commands([
  ["pm", "±"],
  ["mp", "∓"],
  ["times", "×"],
  ["div", "÷"],
  ["cdot", "⋅"],
  ["le", "≤"],
  ["ge", "≥"],
  ["ne", "≠"],
  ["approx", "≈"],
  ["equiv", "≡"],
  ["sim", "∼"],
  ["propto", "∝"],
  ["infty", "∞"],
  ["partial", "∂"],
  ["nabla", "∇"],
  ["in", "∈"],
  ["notin", "∉"],
  ["subset", "⊂"],
  ["subseteq", "⊆"],
  ["cup", "∪"],
  ["cap", "∩"],
  ["forall", "∀"],
  ["exists", "∃"],
  ["to", "→"],
  ["Rightarrow", "⇒"],
  ["Leftrightarrow", "⇔"],
  ["ldots", "…"],
  ["cdots", "⋯"],
]);

export const ACCENT_SNIPPETS: readonly EquationSnippet[] = [
  { id: "hat", label: "x̂", before: "\\hat{", after: "}" },
  { id: "bar", label: "x̄", before: "\\bar{", after: "}" },
  { id: "overline", label: "x̅y̅", before: "\\overline{", after: "}" },
  { id: "vec", label: "x⃗", before: "\\vec{", after: "}" },
  { id: "dot", label: "ẋ", before: "\\dot{", after: "}" },
  { id: "ddot", label: "ẍ", before: "\\ddot{", after: "}" },
  { id: "tilde", label: "x̃", before: "\\tilde{", after: "}" },
  { id: "mathbb", label: "ℝ", before: "\\mathbb{", after: "}" },
  { id: "mathrm", label: "rm", before: "\\mathrm{", after: "}" },
];

export interface Insertion {
  value: string;
  /** Where the cursor goes: inside the first slot, or after the inserted text. */
  cursor: number;
}

/** Insert a snippet over the selection `[start, end)`, wrapping what was selected. */
export function insertSnippet(
  value: string,
  start: number,
  end: number,
  snippet: Pick<EquationSnippet, "before" | "after">
): Insertion {
  const from = Math.max(0, Math.min(start, value.length));
  const to = Math.max(from, Math.min(end, value.length));
  const selected = value.slice(from, to);
  const inserted = `${snippet.before}${selected}${snippet.after}`;
  return {
    value: `${value.slice(0, from)}${inserted}${value.slice(to)}`,
    cursor: from + snippet.before.length + selected.length,
  };
}

const RE_MATH_ELEMENT = /<math[\s\S]*<\/math>/;
const RE_ANNOTATION = /<annotation[\s\S]*?<\/annotation>/g;
const RE_SEMANTICS = /<\/?semantics>/g;

/**
 * The `<math>` element alone, from KaTeX's output, without the `<semantics>` wrapper
 * and the TeX annotation: plain presentation MathML in the MathML namespace, which is
 * what Word recognises when it is pasted as text.
 */
export function mathmlForWord(katexOutput: string): string {
  const math = RE_MATH_ELEMENT.exec(katexOutput)?.[0] ?? "";
  return math.replace(RE_ANNOTATION, "").replace(RE_SEMANTICS, "");
}

/** The `<math>` element with its TeX annotation, for an HTML page. */
export function mathElement(katexOutput: string): string {
  return RE_MATH_ELEMENT.exec(katexOutput)?.[0] ?? "";
}

/** LaTeX ready for a .tex file: `$…$` inline, or display brackets. */
export function withDelimiters(tex: string, display: boolean): string {
  const body = tex.trim();
  return display ? `\\[\n  ${body}\n\\]` : `$${body}$`;
}

/** A small standalone HTML file showing the equation, with its LaTeX kept inside. */
export function equationDocument(
  math: string,
  title: string,
  lang = "en"
): string {
  return `<!DOCTYPE html>
<html lang="${escapeHtml(lang)}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
</head>
<body>
${math}
</body>
</html>
`;
}
